import { useState } from 'react';
import { Search, FileSpreadsheet, Loader2, ExternalLink, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { chatTheme as th } from '../../theme/chat';
import { META_COLS, formatCurrency, formatValue, isNumeric } from './tableUtils';

/**
 * Unified table component for both flat and financial modes.
 *
 * Props:
 *   columns:     string[]          — column keys
 *   rows:        object[]          — row dicts
 *   mode:        "flat"|"financial" — explicit mode (no auto-detect)
 *   title:       string            — display name for toolbar
 *   onCellClick: fn({ column, value, row }) — drill-down (flat mode, numeric cells)
 *   onExport:    fn({ columns, rows, title }) => Promise<url> — export callback
 *   loading:     boolean
 *   emptyMessage: string
 *   chartSlot:   ReactNode         — PnlChartView (financial mode)
 */
export default function DataTable({ columns, rows, mode, title, onCellClick, onExport, loading, emptyMessage, chartSlot }) {
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportUrl, setExportUrl] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);

  const tbl = th.workspace.table;
  const rw = th.workspace.row;

  // Value columns for financial mode (exclude metadata columns)
  const valueCols = mode === 'financial'
    ? (columns || []).filter(c => !META_COLS.has(c))
    : (columns || []);

  // Search filter (flat mode only)
  const filtered = mode === 'flat' && search
    ? (rows || []).filter(row =>
        valueCols.some(col => {
          const val = row[col];
          return val !== undefined && val !== null && String(val).toLowerCase().includes(search.toLowerCase());
        })
      )
    : (rows || []);

  const handleExport = async () => {
    if (!onExport) return;
    setExporting(true);
    setExportUrl(null);
    try {
      const url = await onExport({ columns, rows: filtered, title });
      if (url) {
        setExportUrl(url);
        window.open(url, '_blank');
      }
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 size={16} className="animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  // Empty state
  if (!columns?.length || !filtered?.length) {
    return (
      <div className={cn('flex-1 flex items-center justify-center', th.workspace.emptyState)}>
        {emptyMessage || 'Run a query to see results'}
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar */}
      <div className={cn('flex items-center justify-between px-4 py-2.5 flex-shrink-0', tbl.toolbarBg, tbl.toolbarBorder)}>
        <span className={tbl.toolbarText}>
          {title || 'Results'}
          <span className={cn('ml-2', tbl.toolbarCount)}>
            ({filtered.length} row{filtered.length !== 1 ? 's' : ''})
          </span>
        </span>
        <div className="flex items-center gap-2">
          {/* Search — flat mode only */}
          {mode === 'flat' && (
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className={tbl.searchInput}
              />
            </div>
          )}

          {/* Chart toggle — financial mode only */}
          {chartSlot && (
            <button
              onClick={() => setChartOpen(v => !v)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded-md transition-colors',
                chartOpen
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
              )}
            >
              <BarChart3 size={11} />
              {chartOpen ? 'Hide Chart' : 'KPI Chart'}
              {chartOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </button>
          )}

          {/* Export */}
          {onExport && filtered.length > 0 && (
            <button onClick={handleExport} disabled={exporting} className={tbl.exportBtn}>
              {exporting ? <Loader2 size={11} className="animate-spin" /> : <FileSpreadsheet size={11} />}
              {exporting ? 'Exporting\u2026' : 'Export to Sheet'}
            </button>
          )}
          {exportUrl && (
            <a href={exportUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1 text-xs text-emerald-600 hover:text-emerald-800 transition-colors">
              <ExternalLink size={10} /> Open Sheet
            </a>
          )}
        </div>
      </div>

      {/* Chart panel (financial mode) */}
      {chartOpen && chartSlot && (
        <div className="border-b border-zinc-200 bg-zinc-50/50 px-4 py-3 flex-shrink-0">
          {chartSlot}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {mode === 'financial' ? (
          <FinancialTable valueCols={valueCols} rows={filtered} rw={rw} tbl={tbl} />
        ) : (
          <FlatTable columns={valueCols} rows={filtered} onCellClick={onCellClick} tbl={tbl} />
        )}
      </div>
    </div>
  );
}


/* ── Financial Table ── */

function FinancialTable({ valueCols, rows, rw, tbl }) {
  return (
    <table className="w-full text-xs border-collapse">
      <thead className="sticky top-0 z-10">
        <tr className={cn(tbl.headerBg, tbl.headerBorder)}>
          <th className={cn(
            'text-left px-4 py-2.5 whitespace-nowrap min-w-[240px] sticky left-0 z-20',
            tbl.headerBg, tbl.headerCell
          )}>
            Account
          </th>
          {valueCols.map(col => (
            <th
              key={col}
              className={cn(
                'text-right px-4 py-2.5 whitespace-nowrap min-w-[110px]',
                tbl.headerCell,
                col === 'Total' && tbl.totalColBg,
              )}
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => {
          const rowType = row.row_type || '';
          const depth = row.depth || 0;
          const label = row.label || '';

          if (rowType === 'separator') {
            return (
              <tr key={idx}>
                <td colSpan={valueCols.length + 1} className="py-0.5">
                  <div className={rw.separator} style={{ borderTopWidth: '1px' }} />
                </td>
              </tr>
            );
          }

          return (
            <tr
              key={idx}
              className={cn(
                'transition-colors',
                rowType === 'section_header' && 'bg-zinc-800 text-white',
                rowType === 'account' && rw.account,
                rowType === 'subtotal' && rw.subtotal,
                rowType === 'kpi' && rw.kpi,
              )}
            >
              {/* Label cell */}
              <td
                className={cn(
                  'px-4 py-2 whitespace-nowrap sticky left-0 z-10',
                  rowType === 'section_header' && 'bg-zinc-800 text-white text-[11px] font-bold uppercase tracking-wider py-2.5',
                  rowType === 'account' && 'text-zinc-700 bg-white',
                  rowType === 'subtotal' && 'text-zinc-900 bg-zinc-50 font-semibold',
                  rowType === 'kpi' && 'text-emerald-800 bg-emerald-50/80 font-semibold',
                )}
                colSpan={rowType === 'section_header' ? valueCols.length + 1 : 1}
                style={rowType === 'account' ? { paddingLeft: `${16 + depth * 24}px` } : undefined}
              >
                {label}
              </td>

              {/* Value cells (skip for section headers) */}
              {rowType !== 'section_header' && valueCols.map(col => {
                const val = row[col];
                const num = typeof val === 'number' ? val : Number(val);
                const isNeg = !isNaN(num) && num < 0;

                return (
                  <td
                    key={col}
                    className={cn(
                      'text-right px-4 py-2 whitespace-nowrap tabular-nums',
                      col === 'Total' && rowType === 'account' && 'bg-zinc-50/60',
                      col === 'Total' && rowType === 'subtotal' && 'bg-zinc-200/40',
                      col === 'Total' && rowType === 'kpi' && 'bg-emerald-100/60',
                      rowType === 'subtotal' && 'text-zinc-800',
                      rowType === 'kpi' && 'text-emerald-800',
                      rowType === 'account' && 'text-zinc-700',
                      isNeg && rw.negative,
                    )}
                  >
                    {formatCurrency(val)}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}


/* ── Flat Table ── */

function FlatTable({ columns, rows, onCellClick, tbl }) {
  return (
    <table className="w-full text-xs border-collapse">
      <thead className="sticky top-0 z-10">
        <tr className={cn(tbl.headerBg, tbl.headerBorder)}>
          {columns.map(col => (
            <th
              key={col}
              className={cn(
                'text-left px-4 py-2.5 whitespace-nowrap border-r border-zinc-100 last:border-r-0',
                tbl.headerCell,
              )}
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIdx) => (
          <tr
            key={rowIdx}
            className={cn('border-b border-zinc-100 transition-colors', tbl.hoverRow)}
          >
            {columns.map(col => {
              const val = row[col];
              const numeric = isNumeric(val);
              return (
                <td
                  key={col}
                  className={cn(
                    'px-4 py-2.5 border-r border-zinc-100 last:border-r-0 whitespace-nowrap',
                    tbl.cellText,
                    numeric
                      ? 'text-right font-medium text-zinc-800 cursor-pointer hover:text-accent hover:bg-accent/5 transition-colors'
                      : 'text-left'
                  )}
                  onClick={() => numeric && onCellClick && onCellClick({ column: col, value: val, row })}
                >
                  {formatValue(val, col)}
                </td>
              );
            })}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="text-center py-10 text-zinc-400 text-xs">
              No rows match your search
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
